import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Trophy, Plus, Award, Calendar, TrendingUp, Trash2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from './ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const formatTime = (totalSecs) => {
  const m = Math.floor(totalSecs / 60);
  const s = Math.round(totalSecs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

const formatEntryScore = (entry, scoreType, weightUnit) => {
  if (!entry) return '—';
  const { score, score_subtype, tiebreak_seconds, rounds, extra_reps } = entry;

  if (scoreType === 'for_time') {
    if (score_subtype === 'capped') {
      const reps = extra_reps ?? 0;
      const tb = tiebreak_seconds ? ` + ${formatTime(tiebreak_seconds)} TB` : '';
      return `${reps} reps (cap)${tb}`;
    }
    return formatTime(score);
  }
  if (scoreType === 'amrap') {
    const r = rounds ?? 0;
    const e = extra_reps ?? 0;
    const tb = tiebreak_seconds ? ` | TB: ${formatTime(tiebreak_seconds)}` : '';
    return `${r} rds + ${e} reps${tb}`;
  }
  if (scoreType === 'weight') return `${score} ${weightUnit || 'kg'}`;
  if (scoreType === 'total_reps') return `${score} reps`;
  if (scoreType === 'calories') return `${score} cal`;
  if (scoreType === 'time') return formatTime(score);
  return String(score);
};

const SCORE_TYPE_OPTIONS = [
  { value: 'for_time',   label: 'For Time (completed beats capped)' },
  { value: 'amrap',      label: 'AMRAP (rounds + reps)' },
  { value: 'weight',     label: 'Weight (kg / lbs)' },
  { value: 'total_reps', label: 'Total Reps' },
  { value: 'calories',   label: 'Calories' },
  { value: 'time',       label: 'Time – seconds (lower is better)' },
  { value: 'reps',       label: 'Reps (higher is better, legacy)' },
];

const LOWER_IS_BETTER_TYPES = new Set(['time', 'for_time']);

// ---------------------------------------------------------------------------
// Small reusable UI widgets
// ---------------------------------------------------------------------------

function RxToggle({ value, onChange }) {
  return (
    <div className="flex gap-2">
      {['Rx', 'Scaled'].map((opt) => {
        const active = value ? opt === 'Rx' : opt === 'Scaled';
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt === 'Rx')}
            className={`flex-1 h-9 uppercase text-xs font-bold rounded-sm border transition-colors ${
              active
                ? 'bg-[#CCFF00] text-black border-[#CCFF00]'
                : 'bg-transparent text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]'
            }`}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function CompletionToggle({ value, onChange }) {
  return (
    <div className="flex gap-2">
      {[
        { key: 'completed', label: 'Completed' },
        { key: 'capped',    label: 'Capped (ran out of time)' },
      ].map((opt) => {
        const active = value === opt.key;
        return (
          <button
            key={opt.key}
            type="button"
            onClick={() => onChange(opt.key)}
            className={`flex-1 h-9 text-xs font-bold rounded-sm border transition-colors ${
              active
                ? 'bg-[#CCFF00] text-black border-[#CCFF00]'
                : 'bg-transparent text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]'
            }`}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

function TimeInput({ minsValue, secsValue, onMinsChange, onSecsChange, label = 'TIME', required = true }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">{label}</Label>
      <div className="flex items-center gap-2">
        <Input
          type="number" min="0" step="1"
          value={minsValue}
          onChange={(e) => onMinsChange(e.target.value)}
          placeholder="MM"
          required={required}
          className="bg-[#09090B] border-[#27272A] text-white text-center"
        />
        <span className="text-[#A1A1AA] font-bold text-xl">:</span>
        <Input
          type="number" min="0" max="59" step="1"
          value={secsValue}
          onChange={(e) => onSecsChange(e.target.value)}
          placeholder="SS"
          required={required}
          className="bg-[#09090B] border-[#27272A] text-white text-center"
        />
      </div>
      <p className="text-xs text-[#52525B]">Minutes : Seconds</p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Dynamic score input section for Submit Score dialog
// ---------------------------------------------------------------------------
function ScoreInputSection({ scoreType, form, setForm, weightUnit }) {
  const upd = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  if (scoreType === 'for_time') {
    return (
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Completion</Label>
          <CompletionToggle value={form.scoreSubtype} onChange={(v) => upd('scoreSubtype', v)} />
        </div>

        {form.scoreSubtype === 'completed' ? (
          <TimeInput
            label="Time"
            minsValue={form.mins} secsValue={form.secs}
            onMinsChange={(v) => upd('mins', v)} onSecsChange={(v) => upd('secs', v)}
          />
        ) : (
          <>
            <div className="space-y-1.5">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Reps Completed</Label>
              <Input
                type="number" min="0" step="1"
                value={form.repsCompleted}
                onChange={(e) => upd('repsCompleted', e.target.value)}
                placeholder="e.g. 87"
                required
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <TimeInput
              label="Tiebreak Time"
              required={false}
              minsValue={form.tiebreakMins} secsValue={form.tiebreakSecs}
              onMinsChange={(v) => upd('tiebreakMins', v)} onSecsChange={(v) => upd('tiebreakSecs', v)}
            />
          </>
        )}
      </div>
    );
  }

  if (scoreType === 'amrap') {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Rounds</Label>
            <Input
              type="number" min="0" step="1"
              value={form.rounds}
              onChange={(e) => upd('rounds', e.target.value)}
              placeholder="e.g. 5"
              required
              className="bg-[#09090B] border-[#27272A] text-white"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Extra Reps</Label>
            <Input
              type="number" min="0" step="1"
              value={form.extraReps}
              onChange={(e) => upd('extraReps', e.target.value)}
              placeholder="e.g. 12"
              required
              className="bg-[#09090B] border-[#27272A] text-white"
            />
          </div>
        </div>
        <TimeInput
          label="Tiebreak Time (optional)"
          required={false}
          minsValue={form.tiebreakMins} secsValue={form.tiebreakSecs}
          onMinsChange={(v) => upd('tiebreakMins', v)} onSecsChange={(v) => upd('tiebreakSecs', v)}
        />
      </div>
    );
  }

  if (scoreType === 'weight') {
    return (
      <div className="space-y-1.5">
        <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">
          Weight ({weightUnit || 'kg'})
        </Label>
        <Input
          type="number" step="0.5" min="0"
          value={form.score}
          onChange={(e) => upd('score', e.target.value)}
          placeholder={`e.g. 100 ${weightUnit || 'kg'}`}
          required
          className="bg-[#09090B] border-[#27272A] text-white"
        />
      </div>
    );
  }

  if (scoreType === 'total_reps') {
    return (
      <div className="space-y-1.5">
        <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Total Reps</Label>
        <Input
          type="number" step="1" min="0"
          value={form.score}
          onChange={(e) => upd('score', e.target.value)}
          placeholder="e.g. 150"
          required
          className="bg-[#09090B] border-[#27272A] text-white"
        />
      </div>
    );
  }

  if (scoreType === 'calories') {
    return (
      <div className="space-y-1.5">
        <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Calories</Label>
        <Input
          type="number" step="1" min="0"
          value={form.score}
          onChange={(e) => upd('score', e.target.value)}
          placeholder="e.g. 75"
          required
          className="bg-[#09090B] border-[#27272A] text-white"
        />
      </div>
    );
  }

  // time / reps / legacy
  return (
    <div className="space-y-1.5">
      <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">
        {scoreType === 'time' ? 'Time (seconds)' : 'Score'}
      </Label>
      <Input
        type="number" step="any"
        value={form.score}
        onChange={(e) => upd('score', e.target.value)}
        placeholder={scoreType === 'time' ? 'e.g. 305 for 5:05' : 'Score'}
        required
        className="bg-[#09090B] border-[#27272A] text-white"
      />
      {scoreType === 'time' && (
        <p className="text-xs text-[#52525B]">Enter total seconds</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Leaderboard component
// ---------------------------------------------------------------------------

const emptyScoreForm = () => ({
  scoreSubtype: 'completed',
  mins: '', secs: '',
  repsCompleted: '',
  tiebreakMins: '', tiebreakSecs: '',
  rounds: '', extraReps: '',
  score: '',
  rx: true,
  notes: '',
});

const emptyLbForm = () => ({
  title: '',
  description: '',
  start_date: new Date().toISOString().split('T')[0],
  end_date: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  score_type: 'for_time',
  weight_unit: 'kg',
});

export default function Leaderboard({ user }) {
  const [leaderboards, setLeaderboards] = useState([]);
  const [selectedLeaderboard, setSelectedLeaderboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  const [showLeaderboardForm, setShowLeaderboardForm] = useState(false);
  const [leaderboardFormData, setLeaderboardFormData] = useState(emptyLbForm());

  const [showScoreDialog, setShowScoreDialog] = useState(false);
  const [scoreFormData, setScoreFormData] = useState(emptyScoreForm());

  const [deleteDialog, setDeleteDialog] = useState(null);

  useEffect(() => { fetchLeaderboards(); }, []);

  const fetchLeaderboards = async () => {
    try {
      const res = await axios.get(`${API}/leaderboards`);
      setLeaderboards(res.data);
    } catch {
      toast.error('Failed to load leaderboards');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaderboardDetails = async (id) => {
    try {
      const res = await axios.get(`${API}/leaderboards/${id}`);
      setSelectedLeaderboard(res.data);
    } catch {
      toast.error('Failed to load leaderboard details');
    }
  };

  const createLeaderboard = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        title: leaderboardFormData.title,
        description: leaderboardFormData.description,
        start_date: leaderboardFormData.start_date,
        end_date: leaderboardFormData.end_date,
        score_type: leaderboardFormData.score_type,
        lower_is_better: LOWER_IS_BETTER_TYPES.has(leaderboardFormData.score_type),
        weight_unit: leaderboardFormData.score_type === 'weight' ? leaderboardFormData.weight_unit : null,
      };
      await axios.post(`${API}/leaderboards`, payload);
      toast.success('Leaderboard created!');
      setShowLeaderboardForm(false);
      setLeaderboardFormData(emptyLbForm());
      fetchLeaderboards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create leaderboard');
    }
  };

  const deleteLeaderboard = async () => {
    try {
      await axios.delete(`${API}/leaderboards/${deleteDialog}`);
      toast.success('Leaderboard deleted');
      setDeleteDialog(null);
      setSelectedLeaderboard(null);
      fetchLeaderboards();
    } catch {
      toast.error('Failed to delete leaderboard');
    }
  };

  const buildPayload = (form, scoreType) => {
    let score = 0;
    let score_subtype = null;
    let tiebreak_seconds = null;
    let rounds = null;
    let extra_reps = null;

    const toSecs = (m, s) => (parseInt(m || 0) * 60) + parseInt(s || 0);

    if (scoreType === 'for_time') {
      score_subtype = form.scoreSubtype;
      if (form.scoreSubtype === 'completed') {
        score = toSecs(form.mins, form.secs);
      } else {
        score = parseInt(form.repsCompleted || 0);
        extra_reps = score;
        const tb = toSecs(form.tiebreakMins, form.tiebreakSecs);
        if (tb > 0) tiebreak_seconds = tb;
      }
    } else if (scoreType === 'amrap') {
      rounds = parseInt(form.rounds || 0);
      extra_reps = parseInt(form.extraReps || 0);
      score = rounds * 10000 + extra_reps; // sortable fallback
      const tb = toSecs(form.tiebreakMins, form.tiebreakSecs);
      if (tb > 0) tiebreak_seconds = tb;
    } else {
      score = parseFloat(form.score || 0);
    }

    return {
      leaderboard_id: selectedLeaderboard.leaderboard.id,
      score,
      notes: form.notes || null,
      rx: form.rx,
      score_subtype,
      tiebreak_seconds,
      rounds,
      extra_reps,
    };
  };

  const submitScore = async (e) => {
    e.preventDefault();
    if (!selectedLeaderboard) return;
    const scoreType = selectedLeaderboard.leaderboard.score_type;
    try {
      await axios.post(
        `${API}/leaderboards/${selectedLeaderboard.leaderboard.id}/entries`,
        buildPayload(scoreFormData, scoreType)
      );
      toast.success('Score submitted!');
      setShowScoreDialog(false);
      setScoreFormData(emptyScoreForm());
      fetchLeaderboardDetails(selectedLeaderboard.leaderboard.id);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit score');
    }
  };

  const activeLeaderboards = leaderboards.filter((lb) => lb.is_active);
  const archivedLeaderboards = leaderboards.filter((lb) => !lb.is_active);

  const lb = selectedLeaderboard?.leaderboard;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-[#CCFF00] text-xl font-heading uppercase">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 md:p-12" data-testid="leaderboard-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <h1 className="text-5xl md:text-7xl font-heading font-black uppercase tracking-tighter text-[#FAFAFA] mb-2 flex items-center gap-4">
            <Trophy className="w-12 h-12 md:w-16 md:h-16 text-[#CCFF00]" />
            Leaderboards
          </h1>
          <p className="text-lg text-[#A1A1AA] font-medium">Compete and track your performance</p>
        </div>

        {!selectedLeaderboard ? (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <div className="flex justify-between items-center mb-4">
              <TabsList className="bg-[#18181B] border border-[#27272A]">
                <TabsTrigger value="active" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase">Active</TabsTrigger>
                <TabsTrigger value="archived" className="data-[state=active]:bg-[#CCFF00] data-[state=active]:text-black uppercase">Archived</TabsTrigger>
              </TabsList>
              {(user.role === 'coach' || user.role === 'head_coach') && (
                <Button
                  onClick={() => setShowLeaderboardForm(true)}
                  className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Create Leaderboard
                </Button>
              )}
            </div>

            {['active', 'archived'].map((tab) => {
              const list = tab === 'active' ? activeLeaderboards : archivedLeaderboards;
              return (
                <TabsContent key={tab} value={tab}>
                  {list.length === 0 ? (
                    <Card className="bg-[#18181B] border-[#27272A] p-8">
                      <p className="text-[#A1A1AA] text-center">No {tab} leaderboards</p>
                    </Card>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {list.map((lb) => (
                        <Card
                          key={lb.id}
                          className={`bg-[#18181B] border-[#27272A] p-5 cursor-pointer transition-colors ${tab === 'active' ? 'hover:border-[#CCFF00]/50' : 'hover:border-[#3F3F46] opacity-75'}`}
                          onClick={() => fetchLeaderboardDetails(lb.id)}
                        >
                          <div className="flex items-start justify-between mb-3">
                            <h3 className="text-xl font-heading font-bold uppercase text-[#FAFAFA]">{lb.title}</h3>
                            <span className={`text-xs font-bold px-2 py-1 rounded-sm uppercase ${tab === 'active' ? 'bg-[#22C55E] text-white' : 'bg-[#52525B] text-white'}`}>
                              {tab === 'active' ? 'Active' : 'Ended'}
                            </span>
                          </div>
                          <p className="text-sm text-[#A1A1AA] mb-4 line-clamp-2">{lb.description}</p>
                          <div className="flex items-center gap-4 text-xs text-[#A1A1AA]">
                            <div className="flex items-center gap-1.5">
                              <Calendar className="w-3 h-3" />
                              <span>{tab === 'active' ? 'Ends' : 'Ended'} {new Date(lb.end_date).toLocaleDateString()}</span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <TrendingUp className="w-3 h-3" />
                              <span className="uppercase">{SCORE_TYPE_OPTIONS.find((o) => o.value === lb.score_type)?.label.split('(')[0].trim() || lb.score_type}</span>
                            </div>
                          </div>
                        </Card>
                      ))}
                    </div>
                  )}
                </TabsContent>
              );
            })}
          </Tabs>
        ) : (
          <div>
            <Button
              onClick={() => setSelectedLeaderboard(null)}
              variant="outline"
              className="mb-6 border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]"
            >
              ← Back to Leaderboards
            </Button>

            {/* Leaderboard header card */}
            <Card className="bg-[#18181B] border-[#27272A] p-6 mb-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h2 className="text-3xl font-heading font-black uppercase text-[#FAFAFA]">{lb.title}</h2>
                    <span className={`text-sm font-bold px-3 py-1 rounded-sm uppercase ${lb.is_active ? 'bg-[#22C55E] text-white' : 'bg-[#52525B] text-white'}`}>
                      {lb.is_active ? 'Active' : 'Ended'}
                    </span>
                  </div>
                  <p className="text-[#A1A1AA] mb-4">{lb.description}</p>
                  <div className="flex flex-wrap items-center gap-4 text-sm text-[#A1A1AA]">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span>{new Date(lb.start_date).toLocaleDateString()} – {new Date(lb.end_date).toLocaleDateString()}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-4 h-4" />
                      <span className="uppercase">{SCORE_TYPE_OPTIONS.find((o) => o.value === lb.score_type)?.label || lb.score_type}</span>
                    </div>
                  </div>
                </div>
                <div className="flex gap-2">
                  {lb.is_active && (
                    <Button
                      onClick={() => { setScoreFormData(emptyScoreForm()); setShowScoreDialog(true); }}
                      className="bg-[#CCFF00] text-black font-bold uppercase tracking-wider hover:bg-[#B3E600]"
                      data-testid="submit-score-btn"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Submit Score
                    </Button>
                  )}
                  {(user.role === 'coach' || user.role === 'head_coach') && (
                    <Button
                      onClick={() => setDeleteDialog(lb.id)}
                      variant="outline"
                      className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A] hover:text-[#EF4444]"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>

            {/* Rankings */}
            <Card className="bg-[#18181B] border-[#27272A] p-6">
              <h3 className="text-2xl font-heading font-bold uppercase text-[#FAFAFA] mb-6">Rankings</h3>
              {selectedLeaderboard.entries.length === 0 ? (
                <p className="text-[#A1A1AA] text-center py-8">No entries yet. Be the first to submit a score!</p>
              ) : (
                <div className="space-y-3">
                  {selectedLeaderboard.entries.map((item, index) => (
                    <div
                      key={item.entry.id}
                      className={`flex items-center gap-4 p-4 rounded-sm border ${
                        index === 0 ? 'bg-[#CCFF00]/10 border-[#CCFF00]' :
                        index === 1 ? 'bg-[#A1A1AA]/10 border-[#A1A1AA]' :
                        index === 2 ? 'bg-[#F59E0B]/10 border-[#F59E0B]' :
                        'bg-[#09090B] border-[#27272A]'
                      }`}
                    >
                      <div className="flex items-center justify-center w-12 h-12 flex-shrink-0">
                        {index === 0 && <Trophy className="w-8 h-8 text-[#CCFF00]" />}
                        {index === 1 && <Award className="w-8 h-8 text-[#A1A1AA]" />}
                        {index === 2 && <Award className="w-8 h-8 text-[#F59E0B]" />}
                        {index > 2 && <span className="text-2xl font-heading font-black text-[#52525B]">{index + 1}</span>}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-lg font-semibold text-[#FAFAFA]">{item.user.name}</p>
                          {/* Rx / Scaled badge */}
                          {item.entry.rx === false ? (
                            <span className="text-xs bg-[#A1A1AA]/20 text-[#A1A1AA] px-1.5 py-0.5 rounded-sm uppercase font-bold">Scaled</span>
                          ) : (
                            <span className="text-xs bg-[#CCFF00]/20 text-[#CCFF00] px-1.5 py-0.5 rounded-sm uppercase font-bold">Rx</span>
                          )}
                        </div>
                        {item.entry.notes && (
                          <p className="text-sm text-[#A1A1AA] truncate">{item.entry.notes}</p>
                        )}
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-xl font-heading font-black text-[#FAFAFA]">
                          {formatEntryScore(item.entry, lb.score_type, lb.weight_unit)}
                        </p>
                        {lb.score_type === 'for_time' && item.entry.score_subtype === 'capped' && (
                          <p className="text-xs text-[#F59E0B] uppercase font-bold">Capped</p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          </div>
        )}
      </div>

      {/* ---- Create Leaderboard Dialog ---- */}
      <Dialog open={showLeaderboardForm} onOpenChange={setShowLeaderboardForm}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-black uppercase text-[#FAFAFA]">Create Leaderboard</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">Set up a competition for your athletes</DialogDescription>
          </DialogHeader>
          <form onSubmit={createLeaderboard} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Title</Label>
              <Input
                value={leaderboardFormData.title}
                onChange={(e) => setLeaderboardFormData({ ...leaderboardFormData, title: e.target.value })}
                required placeholder="e.g., CrossFit Open 25.3"
                className="bg-[#09090B] border-[#27272A] text-white"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Description</Label>
              <Textarea
                value={leaderboardFormData.description}
                onChange={(e) => setLeaderboardFormData({ ...leaderboardFormData, description: e.target.value })}
                required placeholder="Describe the competition and scoring..."
                className="bg-[#09090B] border-[#27272A] text-white" rows={3}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Start Date</Label>
                <Input type="date" value={leaderboardFormData.start_date}
                  onChange={(e) => setLeaderboardFormData({ ...leaderboardFormData, start_date: e.target.value })}
                  required className="bg-[#09090B] border-[#27272A] text-white" />
              </div>
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">End Date</Label>
                <Input type="date" value={leaderboardFormData.end_date}
                  onChange={(e) => setLeaderboardFormData({ ...leaderboardFormData, end_date: e.target.value })}
                  required className="bg-[#09090B] border-[#27272A] text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Score Type</Label>
              <select
                value={leaderboardFormData.score_type}
                onChange={(e) => setLeaderboardFormData({ ...leaderboardFormData, score_type: e.target.value, weight_unit: 'kg' })}
                className="w-full bg-[#09090B] border border-[#27272A] text-white rounded-sm h-10 px-3 focus:border-[#CCFF00] focus:outline-none"
                data-testid="lb-score-type-select"
              >
                {SCORE_TYPE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {leaderboardFormData.score_type === 'weight' && (
              <div className="space-y-2">
                <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Weight Unit</Label>
                <div className="flex gap-2">
                  {['kg', 'lbs'].map((u) => (
                    <button
                      key={u}
                      type="button"
                      onClick={() => setLeaderboardFormData({ ...leaderboardFormData, weight_unit: u })}
                      className={`flex-1 h-9 uppercase text-xs font-bold rounded-sm border transition-colors ${
                        leaderboardFormData.weight_unit === u
                          ? 'bg-[#CCFF00] text-black border-[#CCFF00]'
                          : 'bg-transparent text-[#A1A1AA] border-[#27272A] hover:border-[#52525B]'
                      }`}
                    >
                      {u}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowLeaderboardForm(false)} className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]">Cancel</Button>
              <Button type="submit" className="bg-[#CCFF00] text-black font-bold uppercase hover:bg-[#B3E600]">Create Leaderboard</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Submit Score Dialog ---- */}
      <Dialog open={showScoreDialog} onOpenChange={setShowScoreDialog}>
        <DialogContent className="bg-[#18181B] border-[#27272A] max-w-md" data-testid="submit-score-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading font-black uppercase text-[#FAFAFA]">Submit Score</DialogTitle>
            <DialogDescription className="text-[#A1A1AA]">
              {lb && (SCORE_TYPE_OPTIONS.find((o) => o.value === lb.score_type)?.label || lb.score_type)}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitScore} className="space-y-4">
            {lb && (
              <ScoreInputSection
                scoreType={lb.score_type}
                form={scoreFormData}
                setForm={setScoreFormData}
                weightUnit={lb.weight_unit}
              />
            )}

            {/* Rx / Scaled */}
            <div className="space-y-1.5">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Performance</Label>
              <RxToggle value={scoreFormData.rx} onChange={(v) => setScoreFormData((f) => ({ ...f, rx: v }))} />
            </div>

            {/* Notes */}
            <div className="space-y-1.5">
              <Label className="text-[#FAFAFA] uppercase text-xs font-semibold">Notes / Modifications (optional)</Label>
              <Textarea
                value={scoreFormData.notes}
                onChange={(e) => setScoreFormData((f) => ({ ...f, notes: e.target.value }))}
                placeholder="e.g., Used 65 lb bar, subbed pull-ups..."
                className="bg-[#09090B] border-[#27272A] text-white" rows={2}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowScoreDialog(false)} className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]">Cancel</Button>
              <Button type="submit" data-testid="submit-score-confirm-btn" className="bg-[#CCFF00] text-black font-bold uppercase hover:bg-[#B3E600]">Submit Score</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ---- Delete Leaderboard Dialog ---- */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(open) => !open && setDeleteDialog(null)}>
        <AlertDialogContent className="bg-[#18181B] border-[#27272A]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#FAFAFA] font-heading uppercase">Delete Leaderboard</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A1A1AA]">
              Are you sure? All entries will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#27272A] text-[#A1A1AA] hover:bg-[#27272A]">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={deleteLeaderboard} className="bg-[#EF4444] text-white hover:bg-[#DC2626]">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
